import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Avatar, IconButton, Button, Divider, CircularProgress, Alert,
} from '@mui/material';
import { Flag, Delete, Reply } from '@mui/icons-material';
import { BlogComment } from '../../types/blog';
import { useAuth } from '../../contexts/AuthContext';
import CommentService from '../../services/comment-service';
import CommentForm from './CommentForm';

interface CommentSectionProps {
  articleId: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ articleId }) => {
  const { isAuthenticated, user } = useAuth();
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await CommentService.listComments(articleId);
      // Build thread tree: top-level + replies nested one level
      const topLevel = data.filter((c) => !c.parentCommentId && c.status !== 'deleted');
      topLevel.forEach((parent) => {
        parent.replies = data.filter((c) => c.parentCommentId === parent.commentId && c.status !== 'deleted');
      });
      setComments(topLevel);
    } catch {
      setError('Failed to load comments.');
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleCreate = async (body: string, parentCommentId?: string | null) => {
    await CommentService.createComment(articleId, { body, parentCommentId: parentCommentId || null });
    setReplyingTo(null);
    await fetchComments();
  };

  const handleFlag = async (commentId: string) => {
    await CommentService.flagComment(commentId);
    await fetchComments();
  };

  const handleDelete = async (commentId: string) => {
    await CommentService.deleteComment(commentId);
    await fetchComments();
  };

  const isOwnerOrAdmin = (authorId: string) => user?.id === authorId; // admin check would be role-based

  const renderComment = (comment: BlogComment, isReply = false) => (
    <Box key={comment.commentId} sx={{ pl: isReply ? 5 : 0, mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Avatar src={comment.authorAvatar} alt={comment.authorName} sx={{ width: 28, height: 28 }}>
          {comment.authorName?.[0]}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">{comment.authorName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(comment.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{comment.body}</Typography>
          {isAuthenticated && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
              {!isReply && (
                <Button size="small" startIcon={<Reply />} onClick={() => setReplyingTo(comment.commentId)}>
                  Reply
                </Button>
              )}
              <IconButton size="small" onClick={() => handleFlag(comment.commentId)} title="Flag">
                <Flag fontSize="small" />
              </IconButton>
              {isOwnerOrAdmin(comment.authorId) && (
                <IconButton size="small" onClick={() => handleDelete(comment.commentId)} title="Delete">
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
      {replyingTo === comment.commentId && (
        <Box sx={{ pl: 5, mt: 1 }}>
          <CommentForm onSubmit={(body) => handleCreate(body, comment.commentId)} parentCommentId={comment.commentId} placeholder="Write a reply…" />
        </Box>
      )}
    </Box>
  );

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Comments ({comments.length})</Typography>
      <Divider sx={{ mb: 2 }} />

      {isAuthenticated ? (
        <Box sx={{ mb: 3 }}>
          <CommentForm onSubmit={(body) => handleCreate(body)} />
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>Sign in to leave a comment.</Alert>
      )}

      {comments.length === 0 ? (
        <Typography color="text.secondary">No comments yet. Be the first to comment!</Typography>
      ) : (
        comments.map((comment) => (
          <React.Fragment key={comment.commentId}>
            {renderComment(comment)}
            {comment.replies?.map((reply) => renderComment(reply, true))}
          </React.Fragment>
        ))
      )}
    </Box>
  );
};

export default CommentSection;
